import { VIDEO_BASE_URL } from '../config/video';

/** Metadata for one finished-game recording on the storage box. */
export interface Recording {
    matchId: string;
    streamNumber: number;
    /** UTC calendar date of the recording, yyyy-MM-dd. */
    recordingDate: string;
    filename: string;
    size: number;
    contentType: string;
}

/**
 * A matchId is <unix-epoch-seconds><3-digit zero-padded stream number>, so it is
 * digits only and always longer than three characters. Probing anything else is
 * a guaranteed 400 from video-access, so the UI filters those out up front.
 */
const MATCH_ID_PATTERN = /^\d{4,}$/;

const NOT_FOUND = 404;

export const isMatchId = (value?: string | null): value is string =>
    !!value && MATCH_ID_PATTERN.test(value);

export const recordingStreamUrl = (matchId: string): string => `${VIDEO_BASE_URL}/${matchId}/stream`;

export const recordingDownloadUrl = (matchId: string): string => `${VIDEO_BASE_URL}/${matchId}/download`;

/**
 * Resolves the recording for a match. Returns null when the storage box holds no
 * file for that matchId — a normal outcome for a game that was never recorded or
 * has aged out of retention.
 */
export const fetchRecording = async (matchId: string, signal?: AbortSignal): Promise<Recording | null> => {
    const response = await fetch(`${VIDEO_BASE_URL}/${matchId}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal
    });

    if (response.status === NOT_FOUND) {
        return null;
    }
    if (!response.ok) {
        throw new Error(`Recording lookup for ${matchId} failed with status ${response.status}`);
    }
    return response.json() as Promise<Recording>;
};

const BYTES_PER_UNIT = 1024;
const SIZE_UNITS = ['B', 'KB', 'MB', 'GB'];

export const formatFileSize = (bytes: number): string => {
    if (!Number.isFinite(bytes) || bytes <= 0) {
        return '';
    }
    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(BYTES_PER_UNIT)), SIZE_UNITS.length - 1);
    const value = bytes / Math.pow(BYTES_PER_UNIT, unitIndex);
    return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${SIZE_UNITS[unitIndex]}`;
};
